<template>

	<main class="login-content">
		<div class="login-wrap">
			<div class="contact-bg" aria-hidden="true"></div>

			<form v-if="mode === 'login'" class="contact-form login-form" ref="form" @submit.prevent="loginSubmit" name="form" method="post">
				<img class="login-icon-mark" src="images/favicon.png" alt="">
				<h1 class="login-subtitle">Login to access your account</h1>
				<p v-if="message" class="login-alert" role="alert">{{ message }}</p>
				<input class="form-field" type="email" name="username" placeholder="Email *" aria-label="Email" autocomplete="email" required autofocus>
				<div class="password-field">
					<input class="form-field" :type="showPassword ? 'text' : 'password'" name="password" placeholder="Password *" aria-label="Password" autocomplete="current-password" required>
					<button type="button" class="password-toggle" :class="{ 'is-visible': showPassword }"
						:aria-pressed="showPassword" aria-label="Show password" @click="showPassword = !showPassword">
						<svg class="password-toggle__icon-show" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/></svg>
						<svg class="password-toggle__icon-hide" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.38 19.38 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a19.5 19.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
					</button>
				</div>
				<p class="login-forgot"><a href="javascript:void(0);" @click="mode = 'reset-password'">Forgotten your password?</a></p>
				<button type="submit" class="form-submit">Login</button>
			</form>

			<form v-else-if="mode === 'reset-password'" class="contact-form login-form" ref="form" @submit.prevent="resetPasswordSubmit" name="form" method="post">
				<img class="login-icon-mark" src="images/favicon.png" alt="">
				<p class="login-subtitle">Reset your password</p>
				<p class="login-forgot-desc">Enter the email address linked to your account and we&rsquo;ll send you a link to reset your password.</p>
				<input class="form-field" type="email" name="username" placeholder="Email *" aria-label="Email" autocomplete="email" required autofocus>
				<button type="submit" class="form-submit">Send reset link</button>
			</form>

			<div v-else-if="mode === 'reset-password-request-sent'" class="contact-form login-form login-confirmation">
				<img class="login-icon-mark" src="images/favicon.png" alt="">
				<p class="login-subtitle">Check your email</p>
				<p class="login-forgot-desc">If an account exists for that email address, we&rsquo;ve sent a link to reset your password. Please check your inbox and your spam folder.</p>
				<a href="javascript:void(0);" class="form-submit" @click="mode = 'login'">Back to login</a>
			</div>

			<div v-else-if="mode === 'password-changed'" class="contact-form login-form login-confirmation">
				<p class="login-subtitle">Password successfully changed</p>
				<p class="login-forgot-desc">Your password has been updated. You can now log in to your account using your new password.</p>
				<a href="javascript:void(0);" class="form-submit" @click="mode = 'login'">Back to login</a>
			</div>

			<div v-else-if="mode === 'reset-password-expired'" class="contact-form login-form login-confirmation">
				<p class="login-subtitle">This link is no longer valid</p>
				<p class="login-forgot-desc">Your password reset link is invalid or has expired. Please request a new one.</p>
				<a href="javascript:void(0);" class="form-submit" @click="mode = 'reset-password'">Request a new link</a>
			</div>

			<form v-else-if="mode === 'reset-password-new-password'" class="contact-form login-form" ref="form" @submit.prevent="newPasswordSubmit" name="form" method="post">
				<img class="login-icon-mark" src="images/favicon.png" alt="">
				<p class="login-subtitle">Set a new password</p>
				<p class="login-forgot-desc">Choose a new password for your account.</p>
				<input v-model="resetEmail" type="hidden" name="username" />
				<input v-model="resetReference" type="hidden" name="reference" />
				<div class="password-field">
					<input v-model="resetPassword" class="form-field" :type="showNewPassword ? 'text' : 'password'" name="password" placeholder="New password *" aria-label="New password" autocomplete="new-password" required @blur="passwordCheck('password')">
					<button type="button" class="password-toggle" :class="{ 'is-visible': showNewPassword }"
						:aria-pressed="showNewPassword" aria-label="Show password" @click="showNewPassword = !showNewPassword">
						<svg class="password-toggle__icon-show" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/></svg>
						<svg class="password-toggle__icon-hide" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.38 19.38 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a19.5 19.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
					</button>
				</div>
				<div class="password-field">
					<input v-model="resetPasswordReenter" class="form-field" :type="showConfirmPassword ? 'text' : 'password'" name="passwordReenter" placeholder="Confirm new password *" aria-label="Confirm new password" autocomplete="new-password" required @blur="passwordCheck('re-enter')">
					<button type="button" class="password-toggle" :class="{ 'is-visible': showConfirmPassword }"
						:aria-pressed="showConfirmPassword" aria-label="Show password" @click="showConfirmPassword = !showConfirmPassword">
						<svg class="password-toggle__icon-show" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/></svg>
						<svg class="password-toggle__icon-hide" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.38 19.38 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a19.5 19.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
					</button>
				</div>
				<p v-if="passwordError" class="login-alert" role="alert">{{ passwordError }}</p>
				<button type="submit" class="form-submit">Reset password</button>
			</form>

			<p v-if="mode !== 'login'" class="login-signup">Remembered your password? <a href="javascript:void(0);" @click="mode = 'login'">Back to login</a></p>
		</div>
	</main>
</template>

<style lang="scss">
	@use './public-page.scss';
	@use './public-page-whitelabels.scss';
</style>

<script lang="ts">
import { ref, watch, defineComponent, markRaw } from 'vue';
import { block, getUUID } from '@ad/utils/VueBlockUi';

declare global {
	interface Window {
		Login: {
			mode?: string;
			message?: string;

			resetEmail?: string;
			resetReference?: string;
			passwordUnequalFlag?: boolean;
		}
	}
}

const bundle = window.Login;

export default markRaw(defineComponent({
	name: "Login",
	setup() {
		const uuid = getUUID();

		const mode = ref(bundle.mode || 'login');
		const form = ref<HTMLFormElement | null>(null);

		const resetEmail = ref(bundle.resetEmail);
		const resetReference = ref(bundle.resetReference);
		const resetPassword = ref("");
		const resetPasswordReenter = ref("");
		const passwordError = ref<string | undefined>(bundle.passwordUnequalFlag ? 'Please ensure both passwords are identical and at least 8 characters in length.' : undefined);

		const showPassword = ref(false);
		const showNewPassword = ref(false);
		const showConfirmPassword = ref(false);

		watch(mode, newMode => {
			switch (newMode)
			{
			case 'login':
				document.title = "Login | Active Voice";
				break;
			case 'password-changed':
				document.title = "Password Changed | Active Voice";
				break;
			case 'reset-password-expired':
				document.title = "Reset Password | Active Voice";
				break;
			default:
				document.title = "Forgotten Password | Active Voice";
				break;
			}
		}, { immediate: true });

		return {
			mode,
			form,
			message: bundle.message,

			showPassword,
			showNewPassword,
			showConfirmPassword,

			resetEmail,
			resetReference,
			resetPassword,
			resetPasswordReenter,
			passwordError,

			loginSubmit() {
				block("Preparing data. Please wait...", uuid);
				if (form.value)
				{
					form.value.action = 'login-submit';
					form.value.submit();
				}
			},

			resetPasswordSubmit() {
				block("Sending reset link. Please wait...", uuid);
				if (form.value)
				{
					form.value.action = 'reset-password-submit';
					form.value.submit();
				}
			},

			newPasswordSubmit() {
				block("Updating password. Please wait...", uuid);
				if (form.value)
				{
					form.value.action = 'reset-password-request-submit';
					form.value.submit();
				}
			},

			passwordCheck(focusFrom: 'password' | 're-enter') {
				passwordError.value = '';
				if (resetPassword.value.length > 0 && resetPassword.value.length < 8)
				{
					passwordError.value = 'Please ensure the password is at least 8 characters in length.';
				}
				else if (resetPassword.value != resetPasswordReenter.value)
				{
					if (focusFrom == 'password')
					{
						if (resetPasswordReenter.value != '')
						{
							resetPassword.value = '';
							resetPasswordReenter.value = '';
							passwordError.value = 'Please ensure both passwords are identical.';
						}
					}
					else
					{
						resetPassword.value = '';
						resetPasswordReenter.value = '';
						passwordError.value = 'Please ensure both passwords are identical.';
					}
				}
			},
		};
	},
}));

</script>